import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { ArrowLeft, Edit, Send, FileText, Download, Coins, Shield, TrendingUp, Users, Clock, AlertCircle, CheckCircle, XCircle, FilePlus } from 'lucide-react';
import api from '../../lib/api';
import { ProofOfReserveBadge } from '../../components/tokenization';

interface AssetDocument {
  id: string;
  type: string;
  fileName: string;
  fileUrl: string;
  uploadedAt: string;
  status: string;
}

interface LinkedContract {
  id: string;
  title: string;
  status: string;
  category: string;
  createdAt: string;
  signedAt: string | null;
}

interface Asset {
  id: string;
  name: string;
  description: string;
  category: string;
  status: string;
  totalValue: number;
  tokenSymbol: string;
  tokenName: string;
  tokenDecimals: number;
  currentSupply: number;
  maxSupply: number | null;
  unitPrice: number;
  maturityDate: string | null;
  yieldRate: number | null;
  yieldFrequency: string | null;
  isTransferable: boolean;
  requiresWhitelist: boolean;
  metadata: Record<string, string>;
  createdAt: string;
  submittedAt: string | null;
  approvedAt: string | null;
  rejectionReason: string | null;
  documents: AssetDocument[];
  custody: {
    id: string;
    custodianName: string;
    status: string;
    verifiedAt: string | null;
  } | null;
  proofOfReserve: {
    totalReserve: number;
    totalSupply: number;
    coverageRatio: number;
    lastUpdated: string;
    isVerified: boolean;
  } | null;
  holdersCount: number;
  tradingVolume24h: number;
  contracts?: LinkedContract[];
}

const statusConfig: Record<string, { color: string; icon: React.ReactNode; label: string }> = {
  DRAFT: { color: 'bg-surface-600', icon: <Edit className="w-4 h-4" />, label: 'Rascunho' },
  PENDING_APPROVAL: { color: 'bg-yellow-500', icon: <Clock className="w-4 h-4" />, label: 'Aguardando Aprovação' },
  APPROVED: { color: 'bg-green-500', icon: <CheckCircle className="w-4 h-4" />, label: 'Aprovado' },
  REJECTED: { color: 'bg-red-500', icon: <XCircle className="w-4 h-4" />, label: 'Rejeitado' },
  TOKENIZED: { color: 'bg-primary-500', icon: <Coins className="w-4 h-4" />, label: 'Tokenizado' },
  SUSPENDED: { color: 'bg-orange-500', icon: <AlertCircle className="w-4 h-4" />, label: 'Suspenso' },
};

const categoryLabels: Record<string, string> = {
  STABLECOIN: 'Stablecoin',
  RECEIVABLE: 'Recebíveis',
  REAL_ESTATE: 'Imóveis',
  FUND_SHARE: 'Cotas de Fundo',
  FIXED_INCOME: 'Renda Fixa',
  COMMODITY: 'Commodities',
  PRECATORY: 'Precatórios',
};

export function AssetDetails() {
  const { id } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const { data: asset, isLoading } = useQuery({
    queryKey: ['asset', id],
    queryFn: async () => {
      const res = await api.get(`/tokenization/assets/${id}`);
      return res.data as Asset;
    },
    enabled: !!id,
  });

  const submitMutation = useMutation({
    mutationFn: async () => {
      return api.post(`/tokenization/assets/${id}/submit`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['asset', id] });
    },
  });

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL',
    }).format(value / 100);
  };

  const formatNumber = (value: number) => {
    return new Intl.NumberFormat('pt-BR').format(value);
  };

  const formatDate = (date: string) => {
    return new Date(date).toLocaleDateString('pt-BR');
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-2 border-primary-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!asset) {
    return (
      <div className="text-center py-12">
        <p className="text-surface-400">Ativo não encontrado</p>
      </div>
    );
  }

  const status = statusConfig[asset.status];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <button
            onClick={() => navigate(-1)}
            className="p-2 hover:bg-surface-800 rounded-lg transition-colors"
          >
            <ArrowLeft className="w-5 h-5" />
          </button>
          <div>
            <div className="flex items-center gap-3">
              <h1 className="text-2xl font-bold">{asset.name}</h1>
              <span className={`flex items-center gap-1 px-3 py-1 rounded-full text-sm font-medium ${status.color}/20 text-white`}>
                {status.icon}
                {status.label}
              </span>
            </div>
            <p className="text-surface-400">{asset.tokenSymbol} • {categoryLabels[asset.category]}</p>
          </div>
        </div>

        <div className="flex items-center gap-3">
          {/* Create Contract Button - always visible */}
          <Link
            to={`/admin/contracts/new?assetId=${id}`}
            className="flex items-center gap-2 px-4 py-2 bg-purple-600 hover:bg-purple-700 rounded-lg transition-colors"
          >
            <FilePlus className="w-4 h-4" />
            Criar Contrato
          </Link>
          
          {asset.status === 'DRAFT' && (
            <>
              <button
                onClick={() => navigate(`/admin/tokenization/assets/${id}/edit`)}
                className="flex items-center gap-2 px-4 py-2 bg-surface-700 hover:bg-surface-600 rounded-lg transition-colors"
              >
                <Edit className="w-4 h-4" />
                Editar
              </button>
              <button
                onClick={() => submitMutation.mutate()}
                disabled={submitMutation.isPending}
                className="flex items-center gap-2 px-4 py-2 bg-green-600 hover:bg-green-700 rounded-lg transition-colors disabled:opacity-50"
              >
                <Send className="w-4 h-4" />
                {submitMutation.isPending ? 'Enviando...' : 'Enviar para Aprovação'}
              </button>
            </>
          )}
          {asset.status === 'APPROVED' && (
            <button
              onClick={() => navigate(`/admin/tokenization/assets/${id}/token-config`)}
              className="flex items-center gap-2 px-4 py-2 bg-primary-600 hover:bg-primary-700 rounded-lg transition-colors"
            >
              <Coins className="w-4 h-4" />
              Configurar Token
            </button>
          )}
          {asset.status === 'TOKENIZED' && (
            <button
              onClick={() => navigate(`/admin/tokenization/assets/${id}/supply`)}
              className="flex items-center gap-2 px-4 py-2 bg-primary-600 hover:bg-primary-700 rounded-lg transition-colors"
            >
              <Coins className="w-4 h-4" />
              Gerenciar Supply
            </button>
          )}
        </div>
      </div>

      {/* Rejection Reason */}
      {asset.status === 'REJECTED' && asset.rejectionReason && (
        <div className="bg-red-500/10 border border-red-500/30 rounded-xl p-4">
          <div className="flex items-start gap-3">
            <XCircle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
            <div>
              <p className="font-medium text-red-400">Motivo da Rejeição</p>
              <p className="text-surface-300 mt-1">{asset.rejectionReason}</p>
            </div>
          </div>
        </div>
      )}

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="bg-surface-900 rounded-xl p-4 border border-surface-800">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-primary-500/20 rounded-lg">
              <Coins className="w-5 h-5 text-primary-400" />
            </div>
            <div>
              <p className="text-sm text-surface-400">Valor Total</p>
              <p className="text-xl font-bold">{formatCurrency(asset.totalValue)}</p>
            </div>
          </div>
        </div>

        <div className="bg-surface-900 rounded-xl p-4 border border-surface-800">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-green-500/20 rounded-lg">
              <TrendingUp className="w-5 h-5 text-green-400" />
            </div>
            <div>
              <p className="text-sm text-surface-400">Supply Atual</p>
              <p className="text-xl font-bold">
                {formatNumber(asset.currentSupply)}
                {asset.maxSupply && <span className="text-sm text-surface-400"> / {formatNumber(asset.maxSupply)}</span>}
              </p>
            </div>
          </div>
        </div>

        <div className="bg-surface-900 rounded-xl p-4 border border-surface-800">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-blue-500/20 rounded-lg">
              <Users className="w-5 h-5 text-blue-400" />
            </div>
            <div>
              <p className="text-sm text-surface-400">Holders</p>
              <p className="text-xl font-bold">{formatNumber(asset.holdersCount)}</p>
            </div>
          </div>
        </div>

        <div className="bg-surface-900 rounded-xl p-4 border border-surface-800">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-yellow-500/20 rounded-lg">
              <TrendingUp className="w-5 h-5 text-yellow-400" />
            </div>
            <div>
              <p className="text-sm text-surface-400">Volume 24h</p>
              <p className="text-xl font-bold">{formatCurrency(asset.tradingVolume24h)}</p>
            </div>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main Info */}
        <div className="lg:col-span-2 space-y-6">
          {/* Description */}
          <div className="bg-surface-900 rounded-xl p-6 border border-surface-800">
            <h2 className="text-lg font-semibold mb-4">Descrição</h2>
            <p className="text-surface-300">{asset.description || 'Sem descrição'}</p>
          </div>

          {/* Token Details */}
          <div className="bg-surface-900 rounded-xl p-6 border border-surface-800">
            <h2 className="text-lg font-semibold mb-4">Detalhes do Token</h2>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
              <div>
                <p className="text-sm text-surface-400">Nome</p>
                <p className="font-medium">{asset.tokenName}</p>
              </div>
              <div>
                <p className="text-sm text-surface-400">Símbolo</p>
                <p className="font-medium">{asset.tokenSymbol}</p>
              </div>
              <div>
                <p className="text-sm text-surface-400">Decimais</p>
                <p className="font-medium">{asset.tokenDecimals}</p>
              </div>
              <div>
                <p className="text-sm text-surface-400">Preço Unitário</p>
                <p className="font-medium">{formatCurrency(asset.unitPrice)}</p>
              </div>
              <div>
                <p className="text-sm text-surface-400">Transferível</p>
                <p className="font-medium">{asset.isTransferable ? 'Sim' : 'Não'}</p>
              </div>
              <div>
                <p className="text-sm text-surface-400">Whitelist</p>
                <p className="font-medium">{asset.requiresWhitelist ? 'Obrigatória' : 'Não requerida'}</p>
              </div>
            </div>
          </div>

          {/* Yield Info */}
          {asset.yieldRate && (
            <div className="bg-surface-900 rounded-xl p-6 border border-surface-800">
              <h2 className="text-lg font-semibold mb-4">Rendimento</h2>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                <div>
                  <p className="text-sm text-surface-400">Taxa</p>
                  <p className="font-medium text-green-400">{asset.yieldRate}% a.a.</p>
                </div>
                <div>
                  <p className="text-sm text-surface-400">Frequência</p>
                  <p className="font-medium">{asset.yieldFrequency}</p>
                </div>
                {asset.maturityDate && (
                  <div>
                    <p className="text-sm text-surface-400">Vencimento</p>
                    <p className="font-medium">{formatDate(asset.maturityDate)}</p>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Documents */}
          <div className="bg-surface-900 rounded-xl p-6 border border-surface-800">
            <h2 className="text-lg font-semibold mb-4">Documentos</h2>
            {(!asset.documents || asset.documents.length === 0) ? (
              <p className="text-surface-400">Nenhum documento anexado</p>
            ) : (
              <div className="space-y-2">
                {(asset.documents || []).map((doc) => (
                  <div key={doc.id} className="flex items-center justify-between bg-surface-800 rounded-lg px-4 py-3">
                    <div className="flex items-center gap-3">
                      <FileText className="w-5 h-5 text-surface-400" />
                      <div>
                        <p className="font-medium">{doc.fileName}</p>
                        <p className="text-sm text-surface-400">{doc.type} • {formatDate(doc.uploadedAt)}</p>
                      </div>
                    </div>
                    <a
                      href={doc.fileUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="p-2 hover:bg-surface-700 rounded-lg transition-colors"
                    >
                      <Download className="w-4 h-4" />
                    </a>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Linked Contracts */}
          <div className="bg-surface-900 rounded-xl p-6 border border-surface-800">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold">Contratos Vinculados</h2>
              <Link
                to={`/admin/contracts/new?assetId=${id}`}
                className="text-sm text-primary-400 hover:text-primary-300 flex items-center gap-1"
              >
                <FilePlus className="w-4 h-4" />
                Novo
              </Link>
            </div>
            {!asset.contracts || asset.contracts.length === 0 ? (
              <div className="text-center py-6">
                <FileText className="w-10 h-10 mx-auto mb-2 text-surface-600" />
                <p className="text-surface-400 text-sm">Nenhum contrato vinculado</p>
                <Link
                  to={`/admin/contracts/new?assetId=${id}`}
                  className="inline-flex items-center gap-1 mt-3 text-sm text-primary-400 hover:text-primary-300"
                >
                  <FilePlus className="w-4 h-4" />
                  Criar primeiro contrato
                </Link>
              </div>
            ) : (
              <div className="space-y-2">
                {asset.contracts.map((contract) => (
                  <Link
                    key={contract.id}
                    to={`/admin/contracts/${contract.id}`}
                    className="flex items-center justify-between bg-surface-800 rounded-lg px-4 py-3 hover:bg-surface-700 transition-colors"
                  >
                    <div className="flex items-center gap-3">
                      <FileText className="w-5 h-5 text-purple-400" />
                      <div>
                        <p className="font-medium">{contract.title}</p>
                        <p className="text-sm text-surface-400">{contract.category} • {formatDate(contract.createdAt)}</p>
                      </div>
                    </div>
                    <span className={`px-2 py-1 text-xs rounded-full ${
                      contract.status === 'COMPLETED' 
                        ? 'bg-green-500/20 text-green-400' 
                        : contract.status === 'PENDING_SIGNATURES'
                        ? 'bg-yellow-500/20 text-yellow-400'
                        : 'bg-surface-600 text-surface-300'
                    }`}>
                      {contract.status === 'COMPLETED' ? 'Assinado' : 
                       contract.status === 'PENDING_SIGNATURES' ? 'Pendente' : 
                       contract.status === 'DRAFT' ? 'Rascunho' : contract.status}
                    </span>
                  </Link>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* Proof of Reserve */}
          {asset.proofOfReserve && (
            <div className="bg-surface-900 rounded-xl p-6 border border-surface-800">
              <h2 className="text-lg font-semibold mb-4">Proof of Reserve</h2>
              <ProofOfReserveBadge
                coverageRatio={asset.proofOfReserve.coverageRatio}
                lastVerified={asset.proofOfReserve.lastUpdated}
                size="lg"
              />
              <div className="mt-4 space-y-2">
                <div className="flex justify-between text-sm">
                  <span className="text-surface-400">Reserva Total</span>
                  <span>{formatCurrency(asset.proofOfReserve.totalReserve)}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-surface-400">Supply Total</span>
                  <span>{formatNumber(asset.proofOfReserve.totalSupply)}</span>
                </div>
              </div>
            </div>
          )}

          {/* Custody */}
          {asset.custody && (
            <div className="bg-surface-900 rounded-xl p-6 border border-surface-800">
              <h2 className="text-lg font-semibold mb-4">Custódia</h2>
              <div className="space-y-3">
                <div className="flex items-center gap-2">
                  <Shield className="w-5 h-5 text-primary-400" />
                  <span className="font-medium">{asset.custody.custodianName}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-surface-400">Status</span>
                  <span className={asset.custody.status === 'VERIFIED' ? 'text-green-400' : 'text-yellow-400'}>
                    {asset.custody.status}
                  </span>
                </div>
                {asset.custody.verifiedAt && (
                  <div className="flex justify-between text-sm">
                    <span className="text-surface-400">Verificado em</span>
                    <span>{formatDate(asset.custody.verifiedAt)}</span>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Metadata */}
          {asset.metadata && Object.keys(asset.metadata).length > 0 && (
            <div className="bg-surface-900 rounded-xl p-6 border border-surface-800">
              <h2 className="text-lg font-semibold mb-4">Metadados</h2>
              <div className="space-y-2">
                {Object.entries(asset.metadata).map(([key, value]) => (
                  <div key={key} className="flex justify-between text-sm">
                    <span className="text-surface-400">{key}</span>
                    <span>{value}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Timeline */}
          <div className="bg-surface-900 rounded-xl p-6 border border-surface-800">
            <h2 className="text-lg font-semibold mb-4">Histórico</h2>
            <div className="space-y-4">
              <div className="flex items-start gap-3">
                <div className="w-2 h-2 mt-2 rounded-full bg-surface-500" />
                <div>
                  <p className="text-sm font-medium">Criado</p>
                  <p className="text-xs text-surface-400">{formatDate(asset.createdAt)}</p>
                </div>
              </div>
              {asset.submittedAt && (
                <div className="flex items-start gap-3">
                  <div className="w-2 h-2 mt-2 rounded-full bg-yellow-500" />
                  <div>
                    <p className="text-sm font-medium">Enviado para Aprovação</p>
                    <p className="text-xs text-surface-400">{formatDate(asset.submittedAt)}</p>
                  </div>
                </div>
              )}
              {asset.approvedAt && (
                <div className="flex items-start gap-3">
                  <div className="w-2 h-2 mt-2 rounded-full bg-green-500" />
                  <div>
                    <p className="text-sm font-medium">Aprovado</p>
                    <p className="text-xs text-surface-400">{formatDate(asset.approvedAt)}</p>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default AssetDetails;
